import { toJS, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';

import type { IFormNotificationSettings } from '@feathr/blackbox';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useAccount, useStore, useUser } from '@feathr/extender/state';

import { useIframeMessaging } from '../../../hooks';
import { useDonationFormBuilder } from '../useDonationFormBuilder';
import { getEditorUrl } from './EditorIframe.utils';

import * as styles from './EditorIframe.css';

interface IEditorConfigMessage {
  type: 'feathr:editor:config';
  config: Record<string, unknown>;
  notification_settings?: IFormNotificationSettings;
  isDirty: boolean;
}

interface IEditorReadyMessage {
  type: 'feathr:editor:ready';
}

function isEditorConfigMessage(data: unknown): data is IEditorConfigMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:editor:config'
  );
}

function isEditorReadyMessage(data: unknown): data is IEditorReadyMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:editor:ready'
  );
}

function isFontsMessage(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data.type === 'feathr:fonts:request' || data.type === 'feathr:fonts:refresh')
  );
}

function EditorIframe(): JSX.Element {
  const { donationForm, setEditorConfig } = useDonationFormBuilder();

  // Determine if user is a developer (same logic as Development & QA nav link)
  const account = useAccount();
  const user = useUser();
  const { Fonts } = useStore();
  const { isOnProduction } = useBlackboxVersion();
  const showDevelopmentNavUserSetting = user.getSetting('show_dev_nav') ?? false;
  const isDeveloper = !isOnProduction && (account.isTestAccount || showDevelopmentNavUserSetting);

  const { url: editorUrl, origin: editorOrigin } = getEditorUrl(isDeveloper);

  const sendConfigToEditor = useCallback((): void => {
    const config = toJS(donationForm.get('config') ?? {});
    const notificationSettings = toJS(donationForm.get('notification_settings'));

    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'feathr:editor:init',
        config,
        notification_settings: notificationSettings,
        formId: donationForm.id,
        userEmail: user.get('email'),
        showExtendedFundraisingFields: true,
      },
      editorOrigin,
    );
  }, [donationForm, editorOrigin, user]);

  const sendFontsToEditor = useCallback(async (): Promise<void> => {
    const fontsList = Fonts.list();
    await when(() => !fontsList.isPending);

    const accountFonts = fontsList.models.map((font) => ({
      name: font.name,
      family: `'${font.name}', Arial, Helvetica, sans-serif`,
      url: `${BLACKBOX_URL}fonts/${font.id}/css`,
    }));

    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'feathr:fonts:data',
        payload: { accountFonts },
      },
      editorOrigin,
    );
  }, [Fonts, editorOrigin]);

  const handleMessage = useCallback(
    (event: MessageEvent): void => {
      if (isEditorReadyMessage(event.data)) {
        sendConfigToEditor();
        return;
      }

      if (isFontsMessage(event.data)) {
        sendFontsToEditor();
        return;
      }

      if (isEditorConfigMessage(event.data)) {
        setEditorConfig(event.data.config, event.data.notification_settings, event.data.isDirty);
      }
    },
    [sendConfigToEditor, sendFontsToEditor, setEditorConfig],
  );

  const { iframeRef } = useIframeMessaging({
    origin: editorOrigin,
    onMessage: handleMessage,
    sendConfig: sendConfigToEditor,
  });

  return (
    <div className={styles.root}>
      <iframe
        className={styles.iframe}
        ref={iframeRef}
        src={editorUrl}
        title={'Donation Form Editor'}
      />
    </div>
  );
}

export default observer(EditorIframe);
